import { create } from "zustand";
import * as SharedMessageTypes from "../../darvin-shared/schema/zod/agent/message-types.schema";
import { ProjectApi } from "../api/projectApi";
import { useProjectStore } from "../core_stores/useProjectStore";
import { debugLog } from "../utils/logUtils";

interface ProjectConfigActions {
  initialize: () => Promise<void>;
  loadData: () => Promise<void>;
  setOptimisticName: (name: string | null) => void;
  dispose: () => void;
}

interface ProjectConfigState {
  name: string | null;
  packageName: string | null;

  actions: ProjectConfigActions;
}

const useProjectConfigStore = create<ProjectConfigState>()((set) => {
  let unsubscribeLatestMsg: (() => void) | null = null;
  let unsubscribeProjectStatus: (() => void) | null = null;

  async function loadProjectConfig(
    fallbackConfig?: Omit<SharedMessageTypes.ProjectConfig, "message">,
  ) {
    const projectSettings = await ProjectApi.loadProjectSettings(
      useProjectStore.getState().projectId!,
    ).catch((error) => {
      console.log(
        `[DEBUG ${new Date().toISOString()}] ProjectConfigStore - loadProjectConfig - failed to load project settings:`,
        error,
      );
      return {
        app_name: undefined,
        package_name: undefined,
      };
    });
    const projectName =
      projectSettings["app_name"] || fallbackConfig?.projectName;
    const packageName =
      projectSettings["package_name"] || fallbackConfig?.packageName;

    // DEBUG: Track when project config state is updated
    debugLog(
      `[DEBUG ${new Date().toISOString()}] ProjectConfigStore - setting state - name:`,
      projectName,
      "packageName:",
      packageName,
      "fallbackConfig:",
      fallbackConfig,
    );
    set({
      name: projectName,
      packageName: packageName,
    });
  }

  return {
    name: null,
    packageName: null,

    actions: {
      initialize: async () => {
        const projectId = useProjectStore.getState().projectId;
        if (projectId) {
          await loadProjectConfig();
        }
        unsubscribeLatestMsg = useProjectStore.subscribe(
          (state) => state.latestMsg,
          async (latestMsg) => {
            if (
              latestMsg &&
              latestMsg.type ===
                SharedMessageTypes.MessageTypeEnum.enum.projectConfig
            ) {
              await loadProjectConfig(latestMsg.content);
            }
          },
        );
        unsubscribeProjectStatus = useProjectStore.subscribe(
          (state) => state.initialProjectStatus?.config,
          async (config) => {
            const projectName = config?.projectName;
            const packageName = config?.packageName;
            if (config && projectName && packageName) {
              await loadProjectConfig({ projectName, packageName });
            }
          },
          {
            equalityFn: (a, b) =>
              a?.projectName === b?.projectName &&
              a?.packageName === b?.packageName,
          },
        );
      },

      loadData: async () => {
        const projectId = useProjectStore.getState().projectId;
        if (!projectId) {
          return;
        }
        const response = await ProjectApi.loadProjectSettings(projectId);
        set({
          name: response.app_name,
          packageName: response.package_name,
        });
      },

      setOptimisticName: (name: string | null) => {
        if (name) {
          set({ name });
        }
      },

      dispose: () => {
        // DEBUG: Track when project config store is disposed
        debugLog(
          `[DEBUG ${new Date().toISOString()}] ProjectConfigStore - dispose called`,
        );
        if (unsubscribeLatestMsg) {
          unsubscribeLatestMsg();
          unsubscribeLatestMsg = null;
        }
        if (unsubscribeProjectStatus) {
          unsubscribeProjectStatus();
          unsubscribeProjectStatus = null;
        }
        set({
          name: null,
          packageName: null,
        });
      },
    },
  };
});

export const useProjectName = () =>
  useProjectConfigStore((state) => state.name);
export const useProjectPackageName = () =>
  useProjectConfigStore((state) => state.packageName);
export const useProjectConfigActions = () =>
  useProjectConfigStore((state) => state.actions);
